import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  Baby, 
  MessageCircle, 
  Brain, 
  Mic, 
  Utensils, 
  Users, 
  HeartHandshake,
  ArrowRight
} from 'lucide-react';

export const ServicesSection = () => {
  const services = [
    {
      icon: Baby,
      title: 'Sprachentwicklungs­störungen',
      subtitle: 'Kinder',
      description: 'Unterstützung bei verzögerter oder gestörter Sprachentwicklung im Kindesalter. Ich fördere Wortschatz, Grammatik und Sprachverständnis.',
      color: 'primary',
    },
    {
      icon: MessageCircle,
      title: 'Artikulations­störungen',
      subtitle: 'Alle Altersgruppen',
      description: 'Behandlung von Lautbildungsstörungen und undeutlicher Aussprache für eine klare und verständliche Kommunikation.',
      color: 'secondary',
    },
    {
      icon: Brain,
      title: 'Neurologische Sprachstörungen',
      subtitle: 'Erwachsene',
      description: 'Therapie bei Aphasie, Dysarthrie und anderen Sprachstörungen nach Schlaganfall oder neurologischen Erkrankungen.',
      color: 'primary',
    },
    {
      icon: Mic,
      title: 'Stimmstörungen',
      subtitle: 'Alle Altersgruppen',
      description: 'Behandlung bei Heiserkeit, Stimmermüdung oder funktionellen Stimmstörungen – für eine belastbare und klangvolle Stimme.',
      color: 'secondary',
    },
    {
      icon: Utensils,
      title: 'Schluckstörungen',
      subtitle: 'Dysphagie',
      description: 'Diagnostik und Therapie bei Schluckbeschwerden verschiedener Ursachen für eine sichere Nahrungsaufnahme.',
      color: 'primary',
    },
    {
      icon: Users,
      title: 'Therapie für Senioren',
      subtitle: 'Erwachsene & Senioren',
      description: 'Altersgerechte Behandlung von Sprach-, Sprech- und Schluckstörungen im höheren Lebensalter.',
      color: 'secondary',
    },
  ];

  return (
    <section id="leistungen" className="section-padding bg-soft-gradient relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto container-padding relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-primary text-sm font-body font-medium tracking-wide uppercase mb-4">
            Meine Leistungen
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-heading mb-6">
            Leistungsspektrum
          </h2>
          <p className="text-body font-body text-lg leading-relaxed">
            Ich behandel ein breites Spektrum an Sprach-, Sprech-, 
            Stimm- und Schluckstörungen bei Kindern, Jugendlichen und Erwachsenen.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <Card 
              key={service.title}
              className="group bg-card hover:bg-card-elevated border-border hover:border-primary/20 transition-all duration-300 hover-lift overflow-hidden"
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors duration-300 ${
                    service.color === 'primary' 
                      ? 'bg-primary/10 group-hover:bg-primary/20' 
                      : 'bg-secondary/20 group-hover:bg-secondary/30'
                  }`}>
                    <service.icon className={`w-7 h-7 ${
                      service.color === 'primary' ? 'text-primary' : 'text-secondary-dark'
                    }`} />
                  </div>
                  <span className="text-caption text-xs font-body bg-muted px-3 py-1 rounded-full">
                    {service.subtitle}
                  </span>
                </div>
                <CardTitle className="font-heading text-xl font-medium text-heading group-hover:text-primary transition-colors duration-300">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-body font-body text-sm leading-relaxed">
                  {service.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info Card */}
        <Card className="mt-12 bg-primary/5 border-primary/10">
          <CardContent className="p-8 lg:p-10">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
              <div className="w-16 h-16 lg:w-20 lg:h-20 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <HeartHandshake className="w-8 h-8 lg:w-10 lg:h-10 text-primary" />
              </div>
              <div className="text-center lg:text-left flex-1">
                <h3 className="font-heading text-xl lg:text-2xl font-medium text-heading mb-3">
                  Beratung für Angehörige und Eltern
                </h3>
                <p className="text-body font-body leading-relaxed mb-4">
                  Ich beziehe Sie als Angehörige aktiv in den Therapieprozess ein. 
                  Durch gezielte Beratung und praktische Übungen für zu Hause unterstütze 
                  ich Sie dabei, den Therapieerfolg optimal zu fördern.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insurance Info */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-3 bg-card border border-border rounded-full px-6 py-3 shadow-sm">
            <div className="w-3 h-3 bg-success rounded-full" />
            <p className="text-body font-body text-sm">
              <span className="font-medium">Therapie auf ärztliche Verordnung</span> – 
              Ich bin für alle gesetzlichen und privaten Krankenkassen zugelassen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
